import { mockOpportunityRouteData } from '@pages/Opportunity-dashboard/opportunityTransactionsMock';
import { ITransactionOpportunity } from '@sp-types/transactionOpportunityType';
import React, { useEffect, useRef, useState } from 'react';
import ButtonDesigned from '../../../../components/UI-Elements/Buttons/ButtonDesigned';
import { ChevronButton } from '../../../../components/UI-Elements/Buttons/ChevronButton';
import { formatToPriceInDollars, formatToShortString } from '../../../../utils/FormatValue';
import { OpportunityRoutesDataCard } from './components/OpportunityRoutesDataCard';
import { Timestamp } from 'firebase/firestore';

const ACTIVE_TRANSACTION_TEXT = 'Running';
const DATE_MISSING = '-';

interface Props {
  data: ITransactionOpportunity[];
}

export const TransactionTable: React.FC<Props> = ({ data }) => {
  const [selectedTransactionIndex, setSelectedTransactionIndex] = useState<number>();
  const [isTransactionRoutesOverlay, setIsTransactionRoutesOverlay] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isTransactionRoutesOverlay && scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [isTransactionRoutesOverlay]);

  return (
    <div className="flex flex-col flex-1  gap-3 bg-white p-4 shadow-custom  ">
      <div className="flex justify-between  flex-row me-4">
        <p className="text-lg content-center  ">Transaction Table</p>
        <ButtonDesigned style="outline" onButtonClick={() => {}}>
          Add Transaction
        </ButtonDesigned>
      </div>
      <div className=" bg-gray-50  py-1 min-h-10 ps-3   grid grid-cols-13  gap-2  border  me-4  overflow-hidden ">
        {headings.map((item, index) => (
          <div
            key={index}
            className={`col-span-1   content-center  truncate ${
              item.isWithBorder ? 'border-l-4 border-gray-125  ps-3 ' : ''
            }  `}
          >
            <p className={`text-sm text-secondary_text   `}>{item.text}</p>
          </div>
        ))}
      </div>
      <div className="flex flex-1 flex-col relative gap-2 overflow-y-scroll   " ref={scrollRef}>
        {data.map(({ quantity, routesOptionData, buyByIndex, sellByIndex }, index) => {
          const { date: buyDate, price: buyIndexPrice } = buyByIndex;

          const showCasedRoute = routesOptionData[0];
          const { buyingPrice: routeBuyingPrice, routeId, sellingPrice: routeSellingPrice } = showCasedRoute;
          const transactionPriceDiff = (sellByIndex?.price || 0) - buyIndexPrice;
          const routePriceDiff = routeSellingPrice - routeBuyingPrice;
          const strategiesPriceDiff = routePriceDiff - transactionPriceDiff;
          const strategiesPriceDiffInPrec = ((routePriceDiff - transactionPriceDiff) / buyIndexPrice) * 100;
          return (
            <div
              className={`  py-1  px-3 grid grid-cols-13 relative   gap-4 cursor-pointer   min-h-12 ${
                selectedTransactionIndex === index ? 'border-1 border-red' : 'border border-gray-300'
              } ${isTransactionRoutesOverlay && selectedTransactionIndex !== index && 'blur-sm'} `}
              onClick={() => {
                isTransactionRoutesOverlay ? setIsTransactionRoutesOverlay(false) : setSelectedTransactionIndex(index);
              }}
              onDoubleClick={() => {
                setSelectedTransactionIndex(index);
                setIsTransactionRoutesOverlay(true);
              }}
              key={index}
            >
              <div className="col-span-1 truncate  content-center " title={quantity.toString()}>
                {quantity.toLocaleString() + ' ton'}
              </div>
              <div
                className="col-span-1 truncate  content-center "
                title={buyDate ? formatDate(buyDate) : DATE_MISSING}
              >
                {buyDate ? formatDate(buyDate) : DATE_MISSING}
              </div>
              <div className="col-span-1 truncate  content-center" title={formatToPriceInDollars(buyIndexPrice)}>
                {formatToPriceInDollars(buyIndexPrice)}
              </div>
              <div
                className="col-span-1 truncate  content-center"
                title={sellByIndex?.date ? formatDate(sellByIndex.date) : ACTIVE_TRANSACTION_TEXT}
              >
                {sellByIndex?.date ? formatDate(sellByIndex.date) : ACTIVE_TRANSACTION_TEXT}
              </div>
              <div
                className="col-span-1 truncate  content-center"
                title={sellByIndex ? formatToPriceInDollars(sellByIndex.price) : ACTIVE_TRANSACTION_TEXT}
              >
                {sellByIndex ? formatToPriceInDollars(sellByIndex.price) : ACTIVE_TRANSACTION_TEXT}
              </div>
              <div
                className={`col-span-1 truncate   content-center ${
                  transactionPriceDiff > 0 ? 'text-green-flat' : 'text-orange-500'
                } `}
                title={formatToPriceInDollars(transactionPriceDiff)}
              >
                {formatToPriceInDollars(transactionPriceDiff, 1)}
              </div>
              <div className="border-l-4 border-gray-125 col-span-2" title={mockOpportunityRouteData[routeId].name}>
                <div
                  style={{ borderLeft: `4px solid ${mockOpportunityRouteData[routeId].color}` }}
                  className={`  ms-3 ps-1 flex flex-1  h-full content-center items-center  truncate  `}
                >
                  {mockOpportunityRouteData[routeId].name}
                </div>
              </div>
              <div className="col-span-1 truncate  content-center" title={formatToPriceInDollars(routeBuyingPrice)}>
                {formatToPriceInDollars(routeBuyingPrice)}
              </div>
              <div className="col-span-1 truncate  content-center" title={formatToPriceInDollars(routeSellingPrice)}>
                {formatToPriceInDollars(routeSellingPrice)}
              </div>
              <div
                className={`col-span-1 truncate   content-center ${
                  routePriceDiff > 0 ? 'text-green-flat' : 'text-orange-500'
                } `}
                title={formatToPriceInDollars(routePriceDiff)}
              >
                {formatToPriceInDollars(routePriceDiff, 1)}
              </div>

              <div
                className="border-l-4 border-gray-125 col-span-1"
                title={formatToPriceInDollars(strategiesPriceDiff, 1)}
              >
                <div
                  className={`  ps-2 flex flex-1  h-full content-center items-center font-semibold truncate ${
                    strategiesPriceDiff > 0 ? 'text-green-flat' : 'text-orange-500'
                  } `}
                >
                  {formatToPriceInDollars(strategiesPriceDiff, 1)}
                </div>
              </div>
              <div
                className="col-span-1 truncate flex flex-row justify-between items-center "
                title={formatToShortString(strategiesPriceDiffInPrec) + '%'}
              >
                <p className="content-center font-semibold">{formatToShortString(strategiesPriceDiffInPrec) + '%'}</p>
                <div className="h-6 w-6  ">
                  <ChevronButton
                    onButtonClick={() => {
                      setIsTransactionRoutesOverlay(true);
                    }}
                  />
                </div>
              </div>
              {isTransactionRoutesOverlay && selectedTransactionIndex === index && (
                <div
                  className="absolute right-8  -bottom-5  bg-white cursor-default  z-20  translate-y-full  "
                  onDoubleClick={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                >
                  <OpportunityRoutesDataCard
                    closeDiv={() => setIsTransactionRoutesOverlay(false)}
                    data={data[index].routesOptionData}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
      {/* <TransactionTableFooter data={data} /> */}
    </div>
  );
};

const headings: { text: string; isWithBorder?: boolean }[] = [
  { text: 'Quantity' },
  { text: 'Buy date' },
  { text: 'Buy index' },
  { text: 'Sell date' },
  { text: 'Sell index' },
  { text: 'Diff.' },
  { text: 'Route', isWithBorder: true },
  { text: '' },
  { text: 'Buy price' },
  { text: 'Sell price' },
  { text: 'Diff.' },
  { text: 'P/L', isWithBorder: true },
  { text: 'P/L %' },
];

const formatDate = (date: Timestamp): string => {
  return date.toDate().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
  });
};
